import React, { memo } from 'react'

import { classnames, Icon, RotatingSpinner } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  bool, func, node, string,
} from 'prop-types'

import { DEFAULT_IMAGE_GALLERY_FILTERS } from 'Pods/LazyLoaded/ImageGalleryPanel/constants'
import { MOCK_IMAGES } from 'Pods/LazyLoaded/ImageGalleryPanel/Utils/mockImages'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'

import { NOOP } from 'Utils/stableDefaults'

import Columns from './Components/Columns/Columns'
import ImageModal from './Components/ImageModal/ImageModal'
import useGalleryImages from './Hooks/useGalleryImages'
import useGalleryScroll from './Hooks/useGalleryScroll'
import useImageModal from './Hooks/useImageModal'
import useMasonryColumns from './Hooks/useMasonryColumns'

const spinnerSize = { height: '32px', width: '32px' }

// Matches `.image-gallery-panel__columns` in _image_gallery_panel.scss.
const COLUMN_GAP = 6
// Floor for an image's width: the hover actions need the room (see useMasonryColumns).
const MIN_COLUMN_WIDTH = 240

// Images above the fold load eagerly; the rest defer via native lazy loading.
const EAGER_COUNT = 10

// Static rows painted only during critical-CSS extraction, so the populated
// grid's selectors are captured without waiting on the (mock) request.
const EXTRACTION_IMAGES = MOCK_IMAGES.slice(0, 10)

const filterSignature = ({ searchQuery, category }) => `${searchQuery}|${category}`

function Gallery({
  searchQuery = DEFAULT_IMAGE_GALLERY_FILTERS.searchQuery,
  category = DEFAULT_IMAGE_GALLERY_FILTERS.category,
  emptyState = null,
  extractingCoreStyles = false,
  onImageEdit = NOOP,
  onImageDownload = NOOP,
  onImageGenerate3d = NOOP,
}) {
  const t = useTranslation()

  const {
    images,
    hasNextPage,
    handleLoadMore,
    isFetchingNextPage,
    isLoading,
    isRefetching,
    isError,
    activeFilters,
  } = useGalleryImages({ searchQuery, category })

  const [columnsRef, columns] = useMasonryColumns(
    extractingCoreStyles ? EXTRACTION_IMAGES : images,
    { minColumnWidth: MIN_COLUMN_WIDTH, gap: COLUMN_GAP },
  )

  const [scrollRef, sentinelRef] = useGalleryScroll({
    signature: filterSignature(activeFilters),
    hasNextPage,
    isFetchingNextPage,
    onLoadMore: handleLoadMore,
  })

  const modal = useImageModal(images, { hasNextPage, isFetchingNextPage, loadMore: handleLoadMore })
  const cardHandlers = {
    onImageOpen: modal.open, onImageEdit, onImageDownload, onImageGenerate3d,
  }

  // Rendered in every state, not only over the loaded grid: a shared image URL opens the
  // modal straight away, while the first page may still be loading or come back empty.
  const imageModal = modal.openedImage && (
    <ImageModal
      image={modal.openedImage}
      hasPrevious={modal.hasPrevious}
      hasNext={modal.hasNext}
      onPrevious={modal.previous}
      onNext={modal.next}
      onSelect={modal.open}
      onEdit={onImageEdit}
      onDownload={onImageDownload}
      onGenerate3d={onImageGenerate3d}
      onClose={modal.close}
    />
  )

  // Extraction renders synchronously, so the async states never get their
  // styles captured unless every branch paints at once.
  if (extractingCoreStyles) {
    return (
      <>
        <div className="image-gallery-panel__gallery image-gallery-panel__gallery--loading">
          <Icon svgComponent={RotatingSpinner} size={spinnerSize} />
        </div>
        <div className="image-gallery-panel__gallery image-gallery-panel__gallery--error">
          {t('imageGallery.loadError')}
        </div>
        <div className="image-gallery-panel__gallery image-gallery-panel__gallery--empty">
          {emptyState}
        </div>
        <div className="image-gallery-panel__gallery">
          <div className="image-gallery-panel__columns" ref={columnsRef}>
            <Columns columns={columns} eagerCount={EXTRACTION_IMAGES.length} {...cardHandlers} />
          </div>
        </div>
      </>
    )
  }

  if (isLoading) {
    return (
      <div className="image-gallery-panel__gallery image-gallery-panel__gallery--loading">
        <Icon svgComponent={RotatingSpinner} size={spinnerSize} />
        {imageModal}
      </div>
    )
  }

  if (isError) {
    return (
      <div className="image-gallery-panel__gallery image-gallery-panel__gallery--error">
        {t('imageGallery.loadError')}
        {imageModal}
      </div>
    )
  }

  if (images.length === 0) {
    return (
      <div className="image-gallery-panel__gallery image-gallery-panel__gallery--empty">
        {emptyState}
        {imageModal}
      </div>
    )
  }

  return (
    <div className="image-gallery-panel__gallery" ref={scrollRef}>
      <div
        ref={columnsRef}
        className={classnames(
          'image-gallery-panel__columns',
          isRefetching && 'image-gallery-panel__columns--refetching',
        )}
        aria-busy={isRefetching || undefined}
      >
        <Columns columns={columns} eagerCount={EAGER_COUNT} {...cardHandlers} />
      </div>
      {hasNextPage && (
        <div ref={sentinelRef} className="image-gallery-panel__sentinel" aria-hidden="true" />
      )}
      {imageModal}
    </div>
  )
}

Gallery.propTypes = {
  searchQuery: string,
  category: string,
  emptyState: node,
  extractingCoreStyles: bool,
  onImageEdit: func,
  onImageDownload: func,
  onImageGenerate3d: func,
}

export default memo(Gallery)
